import { CheckCircle2, Circle, Loader2, Play, Plus, Sparkles, SquarePen, X } from 'lucide-react'
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { useAthletes } from '../../contexts/AthletesContext'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { theme } from '../../theme/theme'
import type { ScheduledTask } from '../../types/database.tables'
import { Button } from '../common/Button'
import { type TaskExecutionResult, TaskExecutionResultsModal } from '../common/TaskExecutionResultsModal'
import { type AutomationTemplate, automationColors, automationTemplates } from './automationTemplates'

// Extend ScheduledTask to include color and gradient
interface ScheduledTaskWithColor extends ScheduledTask {
  color?: string
  gradient?: string
}

type Periodicity = 'after_workout' | 'daily' | 'weekly' | 'monthly' | 'yearly'

/**
 * Calculate the next execution time based on periodicity and schedule parameters
 */
export function calculateNextExecution(
  periodicity: string,
  scheduleHour: number | null = 9,
  scheduleDayOfWeek: number | null = null,
  scheduleDayOfMonth: number | null = null,
  baseTime: Date = new Date()
): Date | null {
  const now = new Date(baseTime)
  const hour = scheduleHour ?? 9

  // Start with today at the scheduled hour
  const nextExecution = new Date(now)
  nextExecution.setHours(hour, 0, 0, 0)

  switch (periodicity) {
    case 'after_workout':
      // For after_workout, next execution is not scheduled
      return null

    case 'daily':
      // If today's time has passed, move to tomorrow
      if (nextExecution <= now) {
        nextExecution.setDate(nextExecution.getDate() + 1)
      }
      break

    case 'weekly': {
      const targetDay = scheduleDayOfWeek ?? 0
      const currentDay = nextExecution.getDay()
      // Convert Sunday (0) to 6, and Monday-Saturday to 0-5
      const currentDayAdjusted = currentDay === 0 ? 6 : currentDay - 1
      let daysUntilTarget = targetDay - currentDayAdjusted
      if (daysUntilTarget < 0 || (daysUntilTarget === 0 && nextExecution <= now)) {
        daysUntilTarget += 7
      }
      nextExecution.setDate(nextExecution.getDate() + daysUntilTarget)
      break
    }

    case 'monthly': {
      const targetDay = scheduleDayOfMonth ?? 1
      if (targetDay === -1) {
        // Last day of month
        nextExecution.setMonth(nextExecution.getMonth() + 1, 0) // Sets to last day of current month
        if (nextExecution <= now) {
          nextExecution.setMonth(nextExecution.getMonth() + 2, 0) // Move to last day of next month
        }
      } else {
        // First day of month (or specific day)
        nextExecution.setDate(targetDay)
        if (nextExecution <= now) {
          nextExecution.setMonth(nextExecution.getMonth() + 1, targetDay)
        }
      }
      break
    }

    case 'yearly':
      if (nextExecution <= now) {
        nextExecution.setFullYear(nextExecution.getFullYear() + 1)
      }
      break

    default:
      // Default to daily
      if (nextExecution <= now) {
        nextExecution.setDate(nextExecution.getDate() + 1)
      }
  }

  return nextExecution
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ theme }) => theme.colors.background};

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  top: 0;
  z-index: 10;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.md};
  }
`

const ScrollContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.md};
    gap: ${({ theme }) => theme.spacing.lg};
  }
`

const Title = styled.h1`
  font-size: ${({ theme }) => theme.typography.xxl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;

  @media (max-width: 640px) {
    font-size: ${({ theme }) => theme.typography.xl};
  }
`

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`

const TemplateGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const TemplateCard = styled.div<{ $gradient: string }>`
  background: ${({ $gradient }) => $gradient};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
    pointer-events: none;
  }
`

const TemplateCardContent = styled.div`
  position: relative;
  z-index: 1;
  color: white;
`

const TemplateIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const TemplateTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: 600;
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
  color: white;
`

const TemplateDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
`

const TemplateButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }
`

const AutomationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const AutomationCard = styled.div<{ $isActive: boolean; $color?: string; $gradient?: string }>`
  background: ${({ theme, $isActive, $gradient }) =>
    $isActive && $gradient ? $gradient : theme.colors.cardBackground};
  border: 1px solid ${({ theme, $isActive }) => ($isActive ? 'transparent' : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;
  overflow: hidden;

  ${({ $isActive }) =>
    $isActive &&
    `
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
      pointer-events: none;
    }
  `}

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
`

const AutomationCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  position: relative;
  z-index: 1;
`

const AutomationIcon = styled.div<{ $isActive: boolean; $color?: string }>`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background: ${({ $isActive }) => ($isActive ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)')};
  backdrop-filter: ${({ $isActive }) => ($isActive ? 'blur(10px)' : 'none')};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ $isActive }) => ($isActive ? 'white' : 'rgba(255, 255, 255, 0.5)')};
`

const AutomationInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const AutomationTitle = styled.h3<{ $isActive?: boolean }>`
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: 600;
  color: ${({ theme, $isActive }) => ($isActive ? 'white' : theme.colors.text)};
  margin: 0 0 ${({ theme }) => theme.spacing.xs} 0;
`

const AutomationMeta = styled.div<{ $isActive?: boolean }>`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ $isActive }) => ($isActive ? 'rgba(255, 255, 255, 0.9)' : 'var(--color-text-secondary)')};
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  align-items: center;
`

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:not(:last-child)::after {
    content: '•';
    margin-left: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.textMuted};
  }
`

const AutomationActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.md};
  position: relative;
  z-index: 1;
`

const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary'; $isActive?: boolean }>`
  background: ${({ theme, $variant, $isActive }) => {
    if ($isActive) {
      return $variant === 'primary' ? 'rgba(255, 255, 255, 0.2)' : 'transparent'
    }
    return $variant === 'primary' ? theme.colors.primary : 'transparent'
  }};
  color: ${({ theme, $variant, $isActive }) => {
    if ($isActive) {
      return 'white'
    }
    return $variant === 'primary' ? theme.colors.background : theme.colors.primary
  }};
  border: 1px solid ${({ theme, $variant, $isActive }) => {
    if ($isActive) {
      return 'rgba(255, 255, 255, 0.3)'
    }
    return $variant === 'primary' ? theme.colors.primary : theme.colors.border
  }};
  backdrop-filter: ${({ $isActive }) => ($isActive ? 'blur(10px)' : 'none')};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.typography.sm};
  font-weight: 500;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};

  &:hover:not(:disabled) {
    background: ${({ theme, $variant, $isActive }) => {
      if ($isActive) {
        return 'rgba(255, 255, 255, 0.3)'
      }
      return $variant === 'primary' ? theme.colors.primaryDark : theme.colors.surfaceHover
    }};
    border-color: ${({ $isActive }) => ($isActive ? 'rgba(255, 255, 255, 0.5)' : 'inherit')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Modal = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${({ theme }) => theme.spacing.lg};
`

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.cardBackground};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
`

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.lg};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
  z-index: 1;
`

const ModalTitle = styled.h2`
  font-size: ${({ theme }) => theme.typography.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`

const ModalCloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: ${({ theme }) => theme.transitions.default};

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`

const ModalFooter = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  justify-content: flex-end;
  padding: ${({ theme }) => theme.spacing.lg};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  position: sticky;
  bottom: 0;
  background: ${({ theme }) => theme.colors.cardBackground};
`

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg};

  label {
    display: block;
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    font-size: ${({ theme }) => theme.typography.sm};
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textMuted};
  }

  input, select, textarea {
    width: 100%;
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.typography.md};
    background-color: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }

  textarea {
    resize: vertical;
    min-height: 120px;
  }
`

const InlineFieldGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: center;
  flex-wrap: nowrap;

  .field-label {
    font-size: ${({ theme }) => theme.typography.sm};
    color: ${({ theme }) => theme.colors.textSecondary};
    white-space: nowrap;
  }

  select {
    padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.sm};
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    font-size: ${({ theme }) => theme.typography.sm};
    background-color: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text};
    min-width: 0;

    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
    }
  }
`

const CheckboxGroup = styled.div`
  display: flex;
  width: fit-content;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.typography.md};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  padding: ${({ theme }) => theme.spacing.sm};

  input[type="checkbox"] {
    width: auto;
    accent-color: ${({ theme }) => theme.colors.primary};
    margin: 0 0 0 ${({ theme }) => theme.spacing.xs}
  }
  label {
    margin: 0 0 0 ${({ theme }) => theme.spacing.xs}
  }
`

const AthleteSelectionContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
`

const ColorPickerContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  flex-wrap: wrap;
`

const ColorOption = styled.button<{ $color: string; $selected: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ $color }) => $color};
  border: 3px solid ${({ $selected, theme }) => ($selected ? theme.colors.text : 'transparent')};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.default};
  position: relative;

  &:hover {
    transform: scale(1.1);
  }

  &::after {
    content: ${({ $selected }) => ($selected ? "'✓'" : "''")};
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: white;
    font-size: 18px;
    font-weight: bold;
  }
`

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};

  p {
    margin: ${({ theme }) => theme.spacing.sm} 0;
    font-size: ${({ theme }) => theme.typography.md};
  }
`

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
`

const Notification = styled.div<{ $show: boolean }>`
  position: fixed;
  top: 70px;
  right: ${({ theme }) => theme.spacing.lg};
  background-color: ${({ theme }) => theme.colors.success || '#4caf50'};
  color: white;
  padding: ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  transform: translateX(${({ $show }) => ($show ? '0' : '400px')});
  opacity: ${({ $show }) => ($show ? '1' : '0')};
  transition: all 0.3s ease-in-out;
  font-size: ${({ theme }) => theme.typography.md};
  font-weight: 500;

  @media (max-width: 640px) {
    right: ${({ theme }) => theme.spacing.sm};
    left: ${({ theme }) => theme.spacing.sm};
  }
`

export const AutomationView: React.FC = () => {
  const { user } = useAuth()
  const { athletes: athletesData } = useAthletes()
  const [tasks, setTasks] = useState<ScheduledTaskWithColor[]>([])
  const [selectedTask, setSelectedTask] = useState<ScheduledTaskWithColor | null>(null)
  const [originalTask, setOriginalTask] = useState<ScheduledTaskWithColor | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [executingTaskId, setExecutingTaskId] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const titleInputRef = useRef<HTMLInputElement>(null)
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(false)
  const [executionResults, setExecutionResults] = useState<TaskExecutionResult[] | null>(null)
  const { automationId } = useParams<{ automationId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [showNotification, setShowNotification] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState('')

  // Transform athletes data to the format needed by AutomationView
  const athletes = athletesData.map((athlete) => ({
    id: athlete.id,
    name: athlete.name,
  }))

  // Check if the current task has unsaved changes
  const isTaskDirty = (): boolean => {
    if (!selectedTask || !originalTask) return false
    if (selectedTask.id === 'new') return true

    return (
      selectedTask.title !== originalTask.title ||
      selectedTask.description !== originalTask.description ||
      selectedTask.prompt !== originalTask.prompt ||
      selectedTask.periodicity !== originalTask.periodicity ||
      selectedTask.is_active !== originalTask.is_active ||
      selectedTask.email_results !== originalTask.email_results ||
      selectedTask.email_mode !== originalTask.email_mode ||
      selectedTask.schedule_hour !== originalTask.schedule_hour ||
      selectedTask.schedule_day_of_week !== originalTask.schedule_day_of_week ||
      selectedTask.schedule_day_of_month !== originalTask.schedule_day_of_month ||
      selectedTask.color !== originalTask.color ||
      JSON.stringify(selectedTask.target_athlete_ids) !== JSON.stringify(originalTask.target_athlete_ids)
    )
  }

  const formatPeriodicity = (periodicity: string): string => {
    return periodicity.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
  }

  const formatTimeInUserTimezone = (hour: number): string => {
    const now = new Date()
    now.setHours(hour, 0, 0, 0)
    return now.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  }

  const formatDayOfWeek = (dayOfWeek: number): string => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
    return days[dayOfWeek]
  }

  const getTimezoneOffset = (): string => {
    const offset = -new Date().getTimezoneOffset()
    const hours = Math.floor(Math.abs(offset) / 60)
    const minutes = Math.abs(offset) % 60
    const sign = offset >= 0 ? '+' : '-'
    return `UTC${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  const formatAthleteTarget = (targetAthleteIds: string[] | null): string => {
    if (!targetAthleteIds || targetAthleteIds.length === 0) {
      return 'No athletes'
    }
    if (targetAthleteIds.length === 1) {
      const athlete = athletes.find((a) => a.id === targetAthleteIds[0])
      return athlete ? athlete.name : '1 athlete'
    }
    return `${targetAthleteIds.length} athletes`
  }

  const loadTasks = useCallback(async () => {
    if (!user) return

    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from('scheduled_tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Parse color and gradient from description field (stored as JSON)
      const tasksWithColor = (data || []).map((task) => {
        let color = automationColors[0].color // default
        let gradient = automationColors[0].gradient // default
        try {
          if (task.description) {
            const metadata = JSON.parse(task.description)
            if (metadata.color) {
              color = metadata.color
              // Find matching gradient for the color
              const colorMatch = automationColors.find((c) => c.color === metadata.color)
              if (colorMatch) {
                gradient = colorMatch.gradient
              }
            }
          }
        } catch {
          // If description is not JSON, keep default color
        }
        return { ...task, color, gradient }
      })

      setTasks(tasksWithColor)
    } catch (error) {
      console.error('Error loading tasks:', error)
    } finally {
      setIsLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadTasks()
  }, [loadTasks])

  // Handle URL parameter for automation selection
  useEffect(() => {
    if (automationId && tasks.length > 0) {
      const task = tasks.find((t) => t.id === automationId)
      if (task) {
        setSelectedTask(task)
        setOriginalTask(JSON.parse(JSON.stringify(task)))
        setIsModalOpen(true)
      }
    }
  }, [automationId, tasks])

  // Handle unsubscribe action
  useEffect(() => {
    const handleUnsubscribe = async () => {
      const unsubscribe = searchParams.get('unsubscribe')
      if (unsubscribe === 'true' && automationId && selectedTask && selectedTask.id === automationId) {
        if (!selectedTask.is_active) {
          searchParams.delete('unsubscribe')
          setSearchParams(searchParams, { replace: true })
          setNotificationMessage('This automation is already inactive')
          setShowNotification(true)
          setTimeout(() => {
            setShowNotification(false)
          }, 5000)
          return
        }

        try {
          const { error } = await supabase.from('scheduled_tasks').update({ is_active: false }).eq('id', automationId)

          if (error) throw error

          const updatedTask = { ...selectedTask, is_active: false }
          setSelectedTask(updatedTask)
          setOriginalTask(JSON.parse(JSON.stringify(updatedTask)))
          await loadTasks()
          setNotificationMessage('Automation has been deactivated successfully')
          setShowNotification(true)
          searchParams.delete('unsubscribe')
          setSearchParams(searchParams, { replace: true })
          setTimeout(() => {
            setShowNotification(false)
          }, 5000)
        } catch (error) {
          console.error('Error deactivating automation:', error)
          setNotificationMessage('Failed to deactivate automation')
          setShowNotification(true)
          setTimeout(() => {
            setShowNotification(false)
          }, 5000)
        }
      }
    }

    if (automationId && selectedTask && searchParams.get('unsubscribe') === 'true') {
      handleUnsubscribe()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automationId, searchParams, selectedTask, setSearchParams, loadTasks])

  const createFromTemplate = (template: AutomationTemplate) => {
    if (!user) return

    const nextExecution = calculateNextExecution(
      template.periodicity,
      template.scheduleHour,
      template.scheduleDayOfWeek,
      template.scheduleDayOfMonth
    )

    const newTask: ScheduledTaskWithColor = {
      id: 'new',
      user_id: user.id,
      title: template.title,
      description: template.description,
      prompt: template.prompt,
      periodicity: template.periodicity,
      is_active: true,
      email_results: template.emailResults,
      email_mode: template.emailMode,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_executed_at: null,
      last_execution_error: null,
      next_execution_at: nextExecution ? nextExecution.toISOString() : null,
      schedule_hour: template.scheduleHour,
      schedule_day_of_week: template.scheduleDayOfWeek ?? null,
      schedule_day_of_month: template.scheduleDayOfMonth ?? null,
      target_athlete_ids: null,
      color: template.color,
      gradient: template.gradient,
    }

    setSelectedTask(newTask)
    setOriginalTask(null)
    setIsModalOpen(true)

    setTimeout(() => {
      titleInputRef.current?.focus()
    }, 0)
  }

  const createNewTask = () => {
    if (!user) return

    const nextExecution = calculateNextExecution('weekly', 9, 0, null)
    const newTask: ScheduledTaskWithColor = {
      id: 'new',
      user_id: user.id,
      title: '',
      description: '',
      prompt: '',
      periodicity: 'weekly',
      is_active: true,
      email_results: true,
      email_mode: 'always',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      last_executed_at: null,
      last_execution_error: null,
      next_execution_at: nextExecution ? nextExecution.toISOString() : null,
      schedule_hour: 9,
      schedule_day_of_week: 0,
      schedule_day_of_month: null,
      target_athlete_ids: null,
      color: automationColors[0].color,
      gradient: automationColors[0].gradient,
    }
    setSelectedTask(newTask)
    setOriginalTask(null)
    setIsModalOpen(true)

    setTimeout(() => {
      titleInputRef.current?.focus()
    }, 0)
  }

  const saveTask = async () => {
    if (!selectedTask || !user) return

    setIsLoading(true)
    try {
      // Store color in description as JSON metadata
      const metadata = JSON.stringify({ color: selectedTask.color })

      if (selectedTask.id === 'new') {
        const { data, error } = await supabase
          .from('scheduled_tasks')
          .insert({
            user_id: user.id,
            title: selectedTask.title,
            description: metadata,
            prompt: selectedTask.prompt,
            periodicity: selectedTask.periodicity,
            is_active: selectedTask.is_active,
            email_results: selectedTask.email_results,
            email_mode: selectedTask.email_mode,
            schedule_hour: selectedTask.schedule_hour,
            schedule_day_of_week: selectedTask.schedule_day_of_week,
            schedule_day_of_month: selectedTask.schedule_day_of_month,
            target_athlete_ids: selectedTask.target_athlete_ids,
            next_execution_at: selectedTask.next_execution_at,
          })
          .select()
        if (error) throw error
        if (!data || data.length === 0) throw new Error('No data returned from insert')

        setIsModalOpen(false)
        setSelectedTask(null)
        setOriginalTask(null)
        await loadTasks()

        setNotificationMessage('Automation created successfully')
        setShowNotification(true)
        setTimeout(() => setShowNotification(false), 3000)
      } else {
        const { error } = await supabase
          .from('scheduled_tasks')
          .update({
            title: selectedTask.title,
            description: metadata,
            prompt: selectedTask.prompt,
            periodicity: selectedTask.periodicity,
            is_active: selectedTask.is_active,
            email_results: selectedTask.email_results,
            email_mode: selectedTask.email_mode,
            schedule_hour: selectedTask.schedule_hour,
            schedule_day_of_week: selectedTask.schedule_day_of_week,
            schedule_day_of_month: selectedTask.schedule_day_of_month,
            target_athlete_ids: selectedTask.target_athlete_ids,
            next_execution_at: selectedTask.next_execution_at,
          })
          .eq('id', selectedTask.id)

        if (error) throw error

        setIsModalOpen(false)
        setSelectedTask(null)
        setOriginalTask(null)
        await loadTasks()

        setNotificationMessage('Automation updated successfully')
        setShowNotification(true)
        setTimeout(() => setShowNotification(false), 3000)
      }
    } catch (error) {
      console.error('Error saving task:', error)
      setNotificationMessage('Failed to save automation')
      setShowNotification(true)
      setTimeout(() => setShowNotification(false), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const deleteTask = async (taskId: string) => {
    if (!user) return

    setIsLoading(true)
    try {
      const { error } = await supabase.from('scheduled_tasks').delete().eq('id', taskId)

      if (error) throw error

      setIsModalOpen(false)
      setSelectedTask(null)
      setOriginalTask(null)
      await loadTasks()

      setNotificationMessage('Automation deleted successfully')
      setShowNotification(true)
      setTimeout(() => setShowNotification(false), 3000)
    } catch (error) {
      console.error('Error deleting task:', error)
      setNotificationMessage('Failed to delete automation')
      setShowNotification(true)
      setTimeout(() => setShowNotification(false), 3000)
    } finally {
      setIsLoading(false)
    }
  }

  const executeTaskNow = async (task: ScheduledTask) => {
    if (!user) return

    setExecutingTaskId(task.id)
    try {
      const { data, error } = await supabase.functions.invoke('task-scheduler', {
        body: {
          task_id: task.id,
          athlete_id: null,
          is_manual: true,
        },
      })

      if (error) {
        console.error('Failed to execute task:', error)
        alert(`Failed to execute task: ${error.message}`)
        return
      }

      setExecutionResults(data)
      setIsResultsModalOpen(true)
    } catch (error) {
      console.error('Error executing task:', error)
      alert('An error occurred while executing the task')
    } finally {
      setExecutingTaskId(null)
    }
  }

  const openEditModal = (task: ScheduledTask) => {
    setSelectedTask(task)
    setOriginalTask(JSON.parse(JSON.stringify(task)))
    setIsModalOpen(true)
  }

  const closeModal = () => {
    if (isTaskDirty()) {
      const confirm = window.confirm('You have unsaved changes. Are you sure you want to close?')
      if (!confirm) return
    }
    setIsModalOpen(false)
    setSelectedTask(null)
    setOriginalTask(null)
  }

  if (isLoading && tasks.length === 0) {
    return (
      <Container>
        <LoadingState>Loading automations...</LoadingState>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <Title>Automations</Title>
        <Button variant="primary" size="md" onClick={createNewTask}>
          <Plus size={16} />
          Create Custom
        </Button>
      </Header>

      <ScrollContent>
        <Section>
          <SectionHeader>
            <SectionTitle>
              <Sparkles size={20} />
              Templates
            </SectionTitle>
          </SectionHeader>
          <TemplateGrid>
            {automationTemplates.map((template) => (
              <TemplateCard
                key={template.id}
                $gradient={template.gradient}
                onClick={() => createFromTemplate(template)}
              >
                <TemplateCardContent>
                  <TemplateIconWrapper>
                    <template.icon size={24} />
                  </TemplateIconWrapper>
                  <TemplateTitle>{template.title}</TemplateTitle>
                  <TemplateDescription>{template.description}</TemplateDescription>
                  <TemplateButton>
                    <Plus size={16} />
                    Use Template
                  </TemplateButton>
                </TemplateCardContent>
              </TemplateCard>
            ))}
          </TemplateGrid>
        </Section>

        <Section>
          <SectionHeader>
            <SectionTitle>Your Automations</SectionTitle>
          </SectionHeader>
          {tasks.length === 0 ? (
            <EmptyState>
              <p>No automations yet.</p>
              <p>Get started by using a template or creating a custom automation.</p>
            </EmptyState>
          ) : (
            <AutomationGrid>
              {tasks.map((task) => {
                const isActive = task.is_active ?? false
                const isThisTaskExecuting = executingTaskId === task.id
                const isAnyTaskExecuting = executingTaskId !== null

                return (
                  <AutomationCard key={task.id} $isActive={isActive} $color={task.color} $gradient={task.gradient}>
                    <AutomationCardHeader>
                      <AutomationIcon $isActive={isActive} $color={task.color}>
                        {isActive ? <CheckCircle2 size={20} /> : <Circle size={20} />}
                      </AutomationIcon>
                      <AutomationInfo>
                        <AutomationTitle $isActive={isActive}>{task.title}</AutomationTitle>
                        <AutomationMeta $isActive={isActive}>
                          <MetaItem>{formatPeriodicity(task.periodicity)}</MetaItem>
                          {task.schedule_day_of_week !== null && (
                            <MetaItem>{formatDayOfWeek(task.schedule_day_of_week)}</MetaItem>
                          )}
                          {task.schedule_hour !== null && (
                            <MetaItem>{formatTimeInUserTimezone(task.schedule_hour)}</MetaItem>
                          )}
                          <MetaItem>{formatAthleteTarget(task.target_athlete_ids)}</MetaItem>
                        </AutomationMeta>
                      </AutomationInfo>
                    </AutomationCardHeader>
                    <AutomationActions>
                      <ActionButton $variant="primary" $isActive={isActive} onClick={() => openEditModal(task)}>
                        <SquarePen size={14} />
                        Edit
                      </ActionButton>
                      {task.periodicity !== 'after_workout' && (
                        <ActionButton
                          $isActive={isActive}
                          onClick={() => executeTaskNow(task)}
                          disabled={isAnyTaskExecuting}
                        >
                          {isThisTaskExecuting ? (
                            <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                          ) : (
                            <Play size={14} />
                          )}
                          Run Now
                        </ActionButton>
                      )}
                    </AutomationActions>
                  </AutomationCard>
                )
              })}
            </AutomationGrid>
          )}
        </Section>
      </ScrollContent>

      {/* Edit Modal */}
      <Modal $isOpen={isModalOpen} onClick={closeModal}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>{selectedTask?.id === 'new' ? 'Create Automation' : 'Edit Automation'}</ModalTitle>
            <ModalCloseButton onClick={closeModal}>
              <X size={20} />
            </ModalCloseButton>
          </ModalHeader>

          {selectedTask && (
            <>
              <ModalBody>
                <FormGroup>
                  <label htmlFor="title">Name *</label>
                  <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                    <input
                      ref={titleInputRef}
                      type="text"
                      name="title"
                      value={selectedTask.title}
                      onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
                      placeholder="Name your automation"
                    />
                    <CheckboxGroup>
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={selectedTask.is_active ?? false}
                        onChange={(e) => setSelectedTask({ ...selectedTask, is_active: e.target.checked })}
                      />
                      <label htmlFor="is_active">Active</label>
                    </CheckboxGroup>
                  </div>
                </FormGroup>

                <FormGroup style={{ marginBottom: theme.spacing.md }}>
                  <label htmlFor="color">Color</label>
                  <ColorPickerContainer>
                    {automationColors.map((colorOption) => (
                      <ColorOption
                        key={colorOption.color}
                        type="button"
                        $color={colorOption.color}
                        $selected={selectedTask.color === colorOption.color}
                        onClick={() =>
                          setSelectedTask({ ...selectedTask, color: colorOption.color, gradient: colorOption.gradient })
                        }
                        title={colorOption.name}
                      />
                    ))}
                  </ColorPickerContainer>
                </FormGroup>

                <FormGroup style={{ marginBottom: theme.spacing.md }}>
                  <InlineFieldGroup>
                    <select
                      value={selectedTask.periodicity}
                      name="periodicity"
                      onChange={(e) => {
                        const newPeriodicity = e.target.value as Periodicity
                        const scheduleHour = newPeriodicity !== 'after_workout' ? selectedTask.schedule_hour || 9 : null
                        const scheduleDayOfWeek =
                          newPeriodicity === 'weekly' ? selectedTask.schedule_day_of_week || 0 : null
                        const scheduleDayOfMonth =
                          newPeriodicity === 'monthly' ? selectedTask.schedule_day_of_month || 1 : null

                        const nextExecution = calculateNextExecution(
                          newPeriodicity,
                          scheduleHour,
                          scheduleDayOfWeek,
                          scheduleDayOfMonth
                        )
                        const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                        setSelectedTask({
                          ...selectedTask,
                          periodicity: newPeriodicity,
                          schedule_hour: scheduleHour,
                          schedule_day_of_week: scheduleDayOfWeek,
                          schedule_day_of_month: scheduleDayOfMonth,
                          next_execution_at: nextExecutionAt,
                        })
                      }}
                    >
                      <option value="after_workout">After workout upload</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="monthly">Monthly</option>
                    </select>

                    {selectedTask.periodicity === 'weekly' && (
                      <>
                        <span className="field-label">on</span>
                        <select
                          value={selectedTask.schedule_day_of_week ?? 1}
                          onChange={(e) => {
                            const newDayOfWeek = Number(e.target.value)
                            const nextExecution = calculateNextExecution(
                              selectedTask.periodicity,
                              selectedTask.schedule_hour,
                              newDayOfWeek,
                              selectedTask.schedule_day_of_month
                            )
                            const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                            setSelectedTask({
                              ...selectedTask,
                              schedule_day_of_week: newDayOfWeek,
                              next_execution_at: nextExecutionAt,
                            })
                          }}
                        >
                          <option value={0}>Monday</option>
                          <option value={1}>Tuesday</option>
                          <option value={2}>Wednesday</option>
                          <option value={3}>Thursday</option>
                          <option value={4}>Friday</option>
                          <option value={5}>Saturday</option>
                          <option value={6}>Sunday</option>
                        </select>
                      </>
                    )}

                    {selectedTask.periodicity === 'monthly' && (
                      <>
                        <span className="field-label">on</span>
                        <select
                          value={selectedTask.schedule_day_of_month || 1}
                          onChange={(e) => {
                            const newDayOfMonth = Number(e.target.value)
                            const nextExecution = calculateNextExecution(
                              selectedTask.periodicity,
                              selectedTask.schedule_hour,
                              selectedTask.schedule_day_of_week,
                              newDayOfMonth
                            )
                            const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                            setSelectedTask({
                              ...selectedTask,
                              schedule_day_of_month: newDayOfMonth,
                              next_execution_at: nextExecutionAt,
                            })
                          }}
                        >
                          <option value={1}>First day</option>
                          <option value={-1}>Last day</option>
                        </select>
                      </>
                    )}

                    {selectedTask.periodicity !== 'after_workout' && (
                      <>
                        <span className="field-label">at</span>
                        <select
                          value={selectedTask.schedule_hour || 9}
                          onChange={(e) => {
                            const newHour = Number(e.target.value)
                            const nextExecution = calculateNextExecution(
                              selectedTask.periodicity,
                              newHour,
                              selectedTask.schedule_day_of_week,
                              selectedTask.schedule_day_of_month
                            )
                            const nextExecutionAt = nextExecution ? nextExecution.toISOString() : null

                            setSelectedTask({
                              ...selectedTask,
                              schedule_hour: newHour,
                              next_execution_at: nextExecutionAt,
                            })
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={`h_${i.toString()}`} value={i}>
                              {formatTimeInUserTimezone(i)}
                            </option>
                          ))}
                        </select>
                        <span
                          className="field-label"
                          style={{ fontSize: '0.9em', color: 'var(--color-text-secondary)' }}
                        >
                          ({getTimezoneOffset()})
                        </span>
                      </>
                    )}
                  </InlineFieldGroup>
                </FormGroup>

                <FormGroup>
                  <label htmlFor="prompt">Instructions *</label>
                  <textarea
                    value={selectedTask.prompt}
                    name="prompt"
                    onChange={(e) => setSelectedTask({ ...selectedTask, prompt: e.target.value })}
                    placeholder="What would you like the AI to do regularly?"
                  />
                </FormGroup>

                <FormGroup style={{ marginBottom: theme.spacing.md }}>
                  <label htmlFor="target_athlete_ids">For Athletes</label>
                  <AthleteSelectionContainer>
                    {athletes.length === 0 ? (
                      <div style={{ color: 'var(--color-text-secondary)', fontSize: '14px' }}>
                        No athletes connected
                      </div>
                    ) : (
                      athletes.map((athlete) => (
                        <CheckboxGroup key={athlete.id}>
                          <input
                            type="checkbox"
                            id={athlete.id}
                            checked={(selectedTask.target_athlete_ids || []).includes(athlete.id)}
                            onChange={(e) => {
                              const currentIds = selectedTask.target_athlete_ids || []
                              if (e.target.checked) {
                                setSelectedTask({
                                  ...selectedTask,
                                  target_athlete_ids: [...currentIds, athlete.id],
                                })
                              } else {
                                setSelectedTask({
                                  ...selectedTask,
                                  target_athlete_ids: currentIds.filter((id) => id !== athlete.id),
                                })
                              }
                            }}
                          />
                          <label htmlFor={athlete.id}>{athlete.name}</label>
                        </CheckboxGroup>
                      ))
                    )}
                  </AthleteSelectionContainer>
                </FormGroup>

                <FormGroup style={{ marginBottom: theme.spacing.md }}>
                  <CheckboxGroup>
                    <input
                      type="checkbox"
                      id="email_results"
                      checked={selectedTask.email_results ?? false}
                      onChange={(e) => setSelectedTask({ ...selectedTask, email_results: e.target.checked })}
                    />
                    <label htmlFor="email_results">Send results by email</label>
                    {selectedTask.email_results && (
                      <select
                        style={{ fontSize: 14, marginLeft: '8px' }}
                        value={selectedTask.email_mode || 'always'}
                        name="email_mode"
                        onChange={(e) => {
                          setSelectedTask({
                            ...selectedTask,
                            email_mode: e.target.value as 'always' | 'tool',
                          })
                        }}
                      >
                        <option value="always">Always</option>
                        <option value="tool">If requested by the agent</option>
                      </select>
                    )}
                  </CheckboxGroup>
                </FormGroup>
              </ModalBody>

              <ModalFooter>
                {selectedTask.id !== 'new' && (
                  <Button variant="danger" onClick={() => deleteTask(selectedTask.id)} disabled={isLoading}>
                    Delete
                  </Button>
                )}
                <div style={{ flex: 1 }} />
                <Button variant="secondary" onClick={closeModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={saveTask}
                  disabled={
                    !selectedTask.title ||
                    !selectedTask.prompt ||
                    isLoading ||
                    (selectedTask.id !== 'new' && !isTaskDirty())
                  }
                >
                  {isLoading ? 'Saving...' : selectedTask.id === 'new' ? 'Create' : 'Save'}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <TaskExecutionResultsModal
        isOpen={isResultsModalOpen}
        onClose={() => setIsResultsModalOpen(false)}
        results={executionResults}
      />

      <Notification $show={showNotification}>{notificationMessage}</Notification>
    </Container>
  )
}
